import React, { useState, useEffect } from "react";
import {
    Shield, Search, RefreshCw, ArrowRightLeft, ArrowRight, ArrowLeft, CheckCircle2, Ban, BrickWallShield, ChevronUp, ChevronDown,
    Zap, Power, Network, Laptop, Globe, Plug, FolderTree, Layers, Calendar
} from "lucide-react";
import baseapi from "../../../services/baseapi"; // Adjust import path
import { showError } from "../../../utils/toast"; // Adjust import path
import { Pagination } from "./Pagination";

interface FirewallRule {
    id?: number | string;
    unique_id?: string;
    name?: string;
    description?: string;
    action?: string; // Allow / Block
    direction?: string; // In / Out
    enabled?: boolean;
    protocol?: string;
    local_addresses?: string;
    local_ports?: string;
    localports?: string;
    remote_addresses?: string;
    remote_ports?: string;
    remoteports?: string;
    application_name?: string;
    service_name?: string;
    grouping?: string;
    profile?: string;
    edge_traversal?: string;
    created?: string;
}

interface AssetFirewallRulesProps {
    assetId: string | number;
    client_id: string | number;
}

export default function AssetFirewallRules({ assetId, client_id }: AssetFirewallRulesProps) {
    const [rules, setRules] = useState<FirewallRule[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchInput, setSearchInput] = useState("");

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    // Refresh Key (used to force useEffect to re-run when clicking refresh)
    const [refreshKey, setRefreshKey] = useState(0);

    const fetchRules = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;

            const queryObj: any = {
                asset_id: assetId,
                client_id: client_id
            };

            if (searchTerm) {
                queryObj.$or = [
                    { name: { $ilike: `%${searchTerm}%` } },
                    { application_name: { $ilike: `%${searchTerm}%` } },
                    { unique_id: { $ilike: `%${searchTerm}%` } },
                    { service_name: { $ilike: `%${searchTerm}%` } },
                    { grouping: { $ilike: `%${searchTerm}%` } }
                ];
            }

            const params: any = {
                skip,
                limit,
                query: JSON.stringify(queryObj)
            };

            // Add API Sorting if requested
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/asset_firewall_rules", { params });

            if (response.data.status) {
                setRules(response.data.data || []);
                setTotalRecords(response.data.total || 0);
            } else {
                showError(response.data.message || "Failed to load firewall rules");
                setRules([]);
                setTotalRecords(0);
            }
        } catch (error: any) {
            showError(error.message || "Failed to load firewall rules");
            setRules([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (assetId) {
            fetchRules();
        }
        // Added refreshKey to the dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, limit, searchTerm, assetId, sortConfig, refreshKey]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        setSearchTerm(searchInput.trim());
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setCurrentPage(1); // Reset to page 1 on sort
    };

    // Helper for rendering sorting headers with colored icon
    const SortableHeader = ({ label, sortKey, icon }: { label: string; sortKey: string; icon?: React.ReactNode }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className="px-3 py-2 text-[10px] font-bold text-gray-300 uppercase tracking-wider cursor-pointer group select-none whitespace-nowrap"
            >
                <div className="flex items-center gap-1.5">
                    {icon}
                    <span>{label}</span>
                    <div className="flex items-center ml-0.5">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-red-400" />
                            ) : (
                                <ChevronDown size={12} className="text-red-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200 opacity-0 group-hover:opacity-100" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    // const formatUniqueId = (id?: string) => {
    //     if (!id) return "-";
    //     if (id.length <= 8) return `${id}****`;
    //     return `${id.slice(0, 8)}****`;
    // };

    // Helper for Direction Icon
    const getDirectionIcon = (direction?: string) => {
        const dir = direction?.toLowerCase() || '';
        if (dir === 'inbound' || dir === 'in') return <span className="flex items-center text-blue-400 gap-1"><ArrowRight size={12} /> Inbound</span>;
        if (dir === 'outbound' || dir === 'out') return <span className="flex items-center text-orange-400 gap-1"><ArrowLeft size={12} /> Outbound</span>;
        return <span className="flex items-center text-gray-200 gap-1"><ArrowRightLeft size={12} /> Any</span>;
    };

    // Helper for Action Badge
    const getActionBadge = (action?: string) => {
        const act = action?.toLowerCase() || '';
        if (act === 'allow') {
            return (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 whitespace-nowrap">
                    <CheckCircle2 size={10} /> Allow
                </span>
            );
        }
        if (act === 'block') {
            return (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-red-500/10 text-red-400 border border-red-500/20 whitespace-nowrap">
                    <Ban size={10} /> Block
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-gray-600/30 text-gray-300 border border-gray-600 whitespace-nowrap">
                {action || 'Unknown'}
            </span>
        );
    };

    const getStatusBadge = (enabled?: boolean) => {
        if (enabled === true) {
            return (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 whitespace-nowrap">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span> Enabled
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-rose-500/10 text-rose-400 border border-rose-500/20 whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span> Disabled
            </span>
        );
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return "-";
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            return d.toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            });
        } catch {
            return dateStr;
        }
    };

    return (
        <div className="flex flex-col h-full text-gray-200 relative p-2 pb-6">
            {/* Header & Toolbar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-3 border-b border-gray-700 pb-3">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <BrickWallShield className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                        <h2 className="text-sm font-bold text-white leading-tight">Firewall Rules</h2>
                        <p className="text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {totalRecords || '...'} rules configured
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => {
                            // Reset state directly (fetchRules() is no longer called here)
                            setCurrentPage(1);
                            setSearchInput("");
                            setSearchTerm("");
                            // Trigger re-render explicitly if values didn't actually change
                            setRefreshKey(prev => prev + 1);
                        }}
                        disabled={loading}
                        className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>
                    <form onSubmit={handleSearchSubmit} className="relative">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search rules..."
                            className="w-64 bg-gray-900 border border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500   transition-colors h-8"
                        />
                    </form>
                </div>
            </div>

            {/* Table Area */}
            <div className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden flex flex-col relative">
                <div className="flex-1 overflow-x-auto overflow-y-auto custom-scroll">
                    <table className="w-full text-left min-w-[1900px] divide-y divide-gray-800">
                        <thead className="bg-gray-800 sticky top-0 z-10">
                            <tr className="border-b border-gray-700">
                                {/* <SortableHeader label="Unique ID" sortKey="unique_id" icon={<Fingerprint size={13} className="text-purple-400" />} /> */}
                                <SortableHeader label="Rule Name" sortKey="name" icon={<Shield size={13} className="text-red-400" />} />
                                <SortableHeader label="Action" sortKey="action" icon={<Zap size={13} className="text-emerald-400" />} />
                                <SortableHeader label="Direction" sortKey="direction" icon={<ArrowRightLeft size={13} className="text-cyan-400" />} />
                                <SortableHeader label="Status" sortKey="enabled" icon={<Power size={13} className="text-amber-400" />} />
                                <SortableHeader label="Protocol" sortKey="protocol" icon={<Network size={13} className="text-orange-400" />} />
                                <SortableHeader label="Local Addresses" sortKey="local_addresses" icon={<Laptop size={13} className="text-sky-400" />} />
                                <SortableHeader label="Local Ports" sortKey="local_ports" icon={<Plug size={13} className="text-teal-400" />} />
                                <SortableHeader label="Remote Addresses" sortKey="remote_addresses" icon={<Globe size={13} className="text-blue-400" />} />
                                <SortableHeader label="Remote Ports" sortKey="remote_ports" icon={<Plug size={13} className="text-indigo-400" />} />
                                {/* <SortableHeader label="Application Name" sortKey="application_name" icon={<AppWindow size={13} className="text-pink-400" />} />
                                <SortableHeader label="Service Name" sortKey="service_name" icon={<Sliders size={13} className="text-yellow-400" />} /> */}
                                <SortableHeader label="Grouping" sortKey="grouping" icon={<FolderTree size={13} className="text-emerald-300" />} />
                                <SortableHeader label="Profile" sortKey="profile" icon={<Layers size={13} className="text-purple-300" />} />
                                {/* <SortableHeader label="Edge Traversal" sortKey="edge_traversal" icon={<ExternalLink size={13} className="text-fuchsia-400" />} /> */}
                                <SortableHeader label="Created At" sortKey="created" icon={<Calendar size={13} className="text-gray-400" />} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/50">
                            {loading ? (
                                <tr>
                                    <td colSpan={16} className="h-48">
                                        <div className="flex flex-col items-center justify-center space-y-3 h-full">
                                            <div className="w-6 h-6 border-2 border-red-500/20 border-t-red-500 rounded-full animate-spin"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : rules.length === 0 ? (
                                <tr>
                                    <td colSpan={16} className="h-48">
                                        <div className="flex flex-col items-center justify-center space-y-2 h-full">
                                            <Shield size={32} className="text-gray-200 mb-2" />
                                            <p className="text-[11px] font-bold text-gray-200 uppercase tracking-widest">No firewall rules found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                rules.map((rule, idx) => {
                                    const localPort = rule.local_ports || rule.localports || "*";
                                    const remotePort = rule.remote_ports || rule.remoteports || "*";

                                    return (
                                        <tr key={rule.unique_id || rule.id || idx} className="hover:bg-gray-800/40 transition-colors duration-100">
                                            {/* Unique ID */}
                                            {/* <td className="px-3 py-2">
                                                <span
                                                    className="text-[10px] font-mono text-gray-300 font-semibold cursor-help"
                                                    title={rule.unique_id || "-"}
                                                >
                                                    {formatUniqueId(rule.unique_id)}
                                                </span>
                                            </td> */}

                                            {/* Rule Name */}
                                            <td className="px-3 py-2 max-w-xs">
                                                <div className="flex flex-col min-w-0">
                                                    <span className="text-[11px] font-bold text-white truncate" title={rule.name || "-"}>
                                                        {rule.name || "-"}
                                                    </span>
                                                    {rule.description && (
                                                        <span className="text-[9px] text-gray-400 truncate" title={rule.description}>
                                                            {rule.description}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Action */}
                                            <td className="px-3 py-2">
                                                {getActionBadge(rule.action)}
                                            </td>

                                            {/* Direction */}
                                            <td className="px-3 py-2">
                                                <span className="text-[10px] font-bold tracking-wide">
                                                    {getDirectionIcon(rule.direction)}
                                                </span>
                                            </td>

                                            {/* Status */}
                                            <td className="px-3 py-2">
                                                {getStatusBadge(rule.enabled)}
                                            </td>

                                            {/* Protocol */}
                                            <td className="px-3 py-2">
                                                <span className="text-[10px] text-gray-300 font-mono font-semibold">
                                                    {rule.protocol || "*"}
                                                </span>
                                            </td>

                                            {/* Local Addresses */}
                                            <td className="px-3 py-2">
                                                <span className="text-[10px] text-gray-300 font-mono" title={rule.local_addresses || "*"}>
                                                    {rule.local_addresses || "*"}
                                                </span>
                                            </td>

                                            {/* Local Ports */}
                                            <td className="px-3 py-2">
                                                <span className="text-[10px] text-gray-300 font-mono" title={localPort}>
                                                    {localPort}
                                                </span>
                                            </td>

                                            {/* Remote Addresses */}
                                            <td className="px-3 py-2">
                                                <span className="text-[10px] text-gray-300 font-mono" title={rule.remote_addresses || "*"}>
                                                    {rule.remote_addresses || "*"}
                                                </span>
                                            </td>

                                            {/* Remote Ports */}
                                            <td className="px-3 py-2">
                                                <span className="text-[10px] text-gray-300 font-mono" title={remotePort}>
                                                    {remotePort}
                                                </span>
                                            </td>

                                            {/* Application Name */}
                                            {/* <td className="px-3 py-2">
                                                <span className="text-[10px] text-gray-300 truncate max-w-[180px] block" title={rule.application_name || "-"}>
                                                    {rule.application_name || "-"}
                                                </span>
                                            </td> */}

                                            {/* Service Name */}
                                            {/* <td className="px-3 py-2">
                                                <span className="text-[10px] text-gray-300 truncate max-w-[150px] block" title={rule.service_name || "-"}>
                                                    {rule.service_name || "-"}
                                                </span>
                                            </td> */}

                                            {/* Grouping */}
                                            <td className="px-3 py-2">
                                                <span className="text-[10px] text-gray-300 truncate max-w-[180px] block" title={rule.grouping || "-"}>
                                                    {rule.grouping || "-"}
                                                </span>
                                            </td>

                                            {/* Profile */}
                                            <td className="px-3 py-2">
                                                <span className="text-[10px] text-gray-300 font-mono" title={rule.profile || "-"}>
                                                    {rule.profile || "-"}
                                                </span>
                                            </td>

                                            {/* Edge Traversal */}
                                            {/* <td className="px-3 py-2">
                                                <span className="text-[10px] text-gray-300" title={rule.edge_traversal || "-"}>
                                                    {rule.edge_traversal || "-"}
                                                </span>
                                            </td> */}

                                            {/* Created At */}
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <span className="text-[10px] text-gray-300 font-mono" title={rule.created || "-"}>
                                                    {formatDate(`${rule.created}Z`)}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    currentPage={currentPage}
                    totalPages={Math.ceil(totalRecords / limit)}
                    onPageChange={setCurrentPage}
                    itemsPerPage={limit}
                    totalItems={totalRecords}
                    onItemsPerPageChange={(val: number) => {
                        setLimit(val);
                        setCurrentPage(1);
                    }}
                />
            </div>
        </div>
    );
}